/**
 * Attachment metadata as the drawer shows it. Mapped from the server's `KanbanTaskAttachmentDto`
 * (camelCase JSON) returned by `GET {routes.tasks}/{id}/attachments` and by the upload route.
 * Pure module: no axios, no stores — jest-tested.
 */
export type Attachment = {
  id?: number;
  publicId: string;
  url: string;
  fileName: string;
  sizeBytes: number;
  mimeType?: string;
  resourceType: 'raw' | 'video' | 'image';
  format?: string;
  /** ISO-8601 UTC string as sent by the server; '' when unknown (never rendered as a date then). */
  uploadedAt: string;
  uploadedByUserId?: number;
};

/** Wire shape of `SkTech.Server.DTO.kanbanTask.KanbanTaskAttachmentDto` (Newtonsoft camelCase). */
export type KanbanTaskAttachmentDto = {
  id: number;
  taskId: number;
  publicId: string;
  url: string;
  fileName?: string | null;
  sizeBytes?: number | null;
  mimeType?: string | null;
  resourceType?: string | null;
  format?: string | null;
  uploadedAt?: string | null;
  uploadedByUserId?: number | null;
};

const RESOURCE_TYPES: ReadonlyArray<Attachment['resourceType']> = ['raw', 'video', 'image'];

function asResourceType(value: string | null | undefined): Attachment['resourceType'] {
  return (RESOURCE_TYPES as readonly string[]).includes(value ?? '') ? (value as Attachment['resourceType']) : 'raw';
}

export function toAttachment(dto: KanbanTaskAttachmentDto): Attachment {
  return {
    id: dto.id,
    publicId: dto.publicId,
    url: dto.url,
    fileName: dto.fileName || dto.publicId.split('/').pop() || dto.publicId,
    sizeBytes: dto.sizeBytes ?? 0,
    mimeType: dto.mimeType ?? undefined,
    resourceType: asResourceType(dto.resourceType),
    format: dto.format ?? undefined,
    uploadedAt: dto.uploadedAt ?? '',
    uploadedByUserId: dto.uploadedByUserId ?? undefined,
  };
}

/** Equals `KanbanAttachmentRules.MaxBytes` and the `[RequestSizeLimit]` on the upload actions. */
export const MAX_ATTACHMENT_BYTES = 104857600;
export const MAX_ATTACHMENT_MB = 100;

export function exceedsAttachmentLimit(sizeBytes: number): boolean {
  return sizeBytes > MAX_ATTACHMENT_BYTES;
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
